import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../App.css";
import { FaEye, FaEyeSlash, FaUser, FaPhone, FaLock, FaMapMarkerAlt, FaCheck, FaExclamation, FaEnvelope } from "react-icons/fa";
import Swal from "sweetalert2";
import logo from "../assets/images/cropped_circle_image.png";
const API_BASE_URL = process.env.REACT_APP_API_URL;

const SignUp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    password: "",
    address: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [allFieldsValid, setAllFieldsValid] = useState(false);

  // Check if all fields are valid
  useEffect(() => {
    const isFullNameValid = formData.fullName.trim() && /\s/.test(formData.fullName.trim());
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid = emailRegex.test(formData.email);
    const phoneDigits = formData.phoneNumber.replace(/\D/g, '');
    const isPhoneNumberValid = phoneDigits.length === 9 || phoneDigits.length === 10;
    const isPasswordValid = formData.password.length >= 8;

    // const isAddressValid = formData.address.trim().length > 0;

    const isValid = isFullNameValid && isEmailValid && isPhoneNumberValid && isPasswordValid;
    setAllFieldsValid(isValid);

    // Hide terms error if user checks the box
    if (isTermsAccepted && showTermsError) {
      setShowTermsError(false);
    }
  }, [formData, isTermsAccepted, showTermsError]);

  const showSuccessModal = () => {
    Swal.fire({
      icon: "success",
      title: "Success!",
      text: "Account created successfully!",
      showConfirmButton: false,
      timer: 5000,
      timerProgressBar: true,
      customClass: {
        popup: "animated fadeInDown",
      },
    }).then(() => {
      navigate("/login");
    });
  };

  const showTermsAlert = () => {
    Swal.fire({
      icon: "warning",
      title: "Terms & Conditions Required",
      html: `
        <div style="text-align: left;">
          <p>You must accept our <strong>Terms & Conditions</strong> and <strong>Privacy Policy</strong> to create an account.</p>
          <p>Please:</p>
          <ol style="margin-left: 20px; margin-top: 10px;">
            <li>Read the <a href="/terms.html" target="_blank" style="color: #3498db; text-decoration: none;">Terms & Conditions</a></li>
            <li>Read the <a href="/privacy-policy.html" target="_blank" style="color: #3498db; text-decoration: none;">Privacy Policy</a></li>
            <li>Check the checkbox to accept them</li>
          </ol>
        </div>
      `,
      showCancelButton: false,
      confirmButtonText: "OK, I understand",
      confirmButtonColor: "#3498db",
      customClass: {
        popup: "animated fadeInDown",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        // Scroll to terms checkbox
        const termsCheckbox = document.getElementById('termsCheck');
        if (termsCheckbox) {
          termsCheckbox.scrollIntoView({ behavior: 'smooth', block: 'center' });
          termsCheckbox.focus();
        }
      }
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleTermsCheckbox = (e) => {
    setIsTermsAccepted(e.target.checked);
    if (e.target.checked && showTermsError) {
      setShowTermsError(false);
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    // Validate full name
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
      isValid = false;
    } else if (!/\s/.test(formData.fullName.trim())) {
      // Check for at least one space
      newErrors.fullName = "Full name must contain at least one space";
      isValid = false;
    }

    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
        isValid = false;
      }
    }

    // Validate phone number
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
      isValid = false;
    } else {
      // Remove hyphens before checking length
      const phoneDigits = formData.phoneNumber.replace(/\D/g, '');
      if (phoneDigits.length !== 9 && phoneDigits.length !== 10) {
        newErrors.phoneNumber = "Please enter a valid 9 or 10-digit phone number";
        isValid = false;
      }
    }

    // Validate password
    if (!formData.password) {
      newErrors.password = "Password is required";
      isValid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
      isValid = false;
    }

    // Validate address
    // if (!formData.address.trim()) {
    //   newErrors.address = "Address is required";
    //   isValid = false;
    // }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // First validate all form fields
    const isFormValid = validateForm();

    if (!isFormValid) {
      // If form fields are invalid, show those errors
      setIsLoading(false);
      return;
    }

    // If form fields are valid but terms not accepted
    if (!isTermsAccepted) {
      setShowTermsError(true);
      showTermsAlert(); // Show alert dialog
      setIsLoading(false);
      return;
    }

    // All validations passed, proceed with signup
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/signup`,
        formData
      );
      if (response.data.success) {
        showSuccessModal();
      }
    } catch (error) {
      console.error("Signup error:", error.response?.data);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: error.response?.data?.message || "Failed to create account. Please try again later.",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
        customClass: {
          popup: "animated fadeInDown",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');

    // Apply formatting: XXX-XXX-XXXX for 10 digits, XX-XXX-XXXX for 9 digits
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

    if (match) {
      return [match[1], match[2], match[3]]
        .filter(group => group)
        .join('-');
    }
    return '';
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const navigateToLogin = () => {
    navigate("/login");
  };

  // Helper functions for validation states
  const isFullNameValid = () => {
    return formData.fullName.trim() && /\s/.test(formData.fullName.trim());
  };

  const isEmailValid = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(formData.email);
  };

  const isPhoneNumberValid = () => {
    const phoneDigits = formData.phoneNumber.replace(/\D/g, '');
    return phoneDigits.length === 9 || phoneDigits.length === 10;
  };

  const isPasswordValid = () => {
    return formData.password.length >= 8;
  };

  return (
    <div className="signup-container" style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
      <div className="signup-form">
        <div className="login-header">
          <div className="logo-container">
            <img src={logo} alt="Company Logo" className="login-logo" />
          </div>
        </div>
        <form onSubmit={handleSubmit} className="login-form signup">
          <div className="input-group">
            <div className="input-icon mt-t">
              <FaUser />
            </div>
            <input
              type="text"
              name="fullName"
              className={`form-input ${errors.fullName ? 'error' : isFullNameValid() ? 'success' : ''}`}
              placeholder="Enter your full name"
              value={formData.fullName}
              onChange={handleChange}
            />
            {/* Validation icon for full name */}
            {formData.fullName.length > 0 && (
              <span className="validation-icon">
                {isFullNameValid() ? (
                  <FaCheck className="text-success" />
                ) : (
                  <FaExclamation className="text-danger" />
                )}
              </span>
            )}
            {errors.fullName && (
              <span className="error-message">{errors.fullName}</span>
            )}
          </div>

          <div className="input-group">
            <div className="input-icon mt-t">
              <FaEnvelope />
            </div>
            <input
              type="email"
              name="email"
              className={`form-input ${errors.email ? 'error' : isEmailValid() ? 'success' : ''}`}
              placeholder="Enter your email address"
              value={formData.email}
              onChange={handleChange}
            />
            {formData.email.length > 0 && (
              <span className="validation-icon">
                {isEmailValid() ? (
                  <FaCheck className="text-success" />
                ) : (
                  <FaExclamation className="text-danger" />
                )}
              </span>
            )}
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          <div className="input-group">
            <div className="input-icon mt-t">
              <FaPhone />
            </div>
            <input
              type="text"
              name="phoneNumber"
              className={`form-input ${errors.phoneNumber ? 'error' : isPhoneNumberValid() ? 'success' : ''}`}
              placeholder="Enter your phone number"
              value={formData.phoneNumber}
              onChange={(e) => {
                const value = e.target.value;
                // Format the phone number while typing
                const formatted = formatPhoneNumber(value);

                // Allow up to 12 characters (for 10 digits + 2 hyphens)
                if (formatted.length <= 12) {
                  setFormData({
                    ...formData,
                    phoneNumber: formatted
                  });
                }

                // Clear error for this field when user starts typing
                if (errors.phoneNumber) {
                  setErrors({ ...errors, phoneNumber: "" });
                }
              }}
              maxLength={12} // 10 digits + 2 hyphens
            />
            {/* Validation icon for phone number */}
            {formData.phoneNumber.length > 0 && (
              <span className="validation-icon">
                {isPhoneNumberValid() ? (
                  <FaCheck className="text-success" />
                ) : (
                  <FaExclamation className="text-danger" />
                )}
              </span>
            )}
            {errors.phoneNumber && (
              <span className="error-message">{errors.phoneNumber}</span>
            )}
          </div>

          <div className="input-group">
            <div className="input-icon mt-t">
              <FaLock />
            </div>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              className={`form-input ${errors.password ? 'error' : isPasswordValid() ? 'success' : ''}`}
              placeholder="Create your password"
              value={formData.password}
              onChange={handleChange}
            />
            <span
              className="password-toggle"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
            {/* Validation icon for password */}
            {formData.password.length > 0 && (
              <span className="validation-icon">
                {isPasswordValid() ? (
                  <FaCheck className="text-success" />
                ) : (
                  <FaExclamation className="text-danger" />
                )}
              </span>
            )}
            {errors.password && (
              <span className="error-message">{errors.password}</span>
            )}
          </div>

          {/* Address Field - Now Optional */}
          <div className="input-group">
            <div className="input-icon mt-t">
              <FaMapMarkerAlt />
            </div>
            <input
              type="text"
              name="address"
              className={`form-input`}
              placeholder="Enter address (optional)" // ✅ UPDATE placeholder
              value={formData.address}
              onChange={handleChange}
            />
          </div>

          {/* Terms & Conditions Checkbox */}
          <div className="d-flex align-items-start gap-2">
            <input
              type="checkbox"
              id="termsCheck"
              checked={isTermsAccepted}
              onChange={handleTermsCheckbox}
              className="mt-1 check-box-input"
            />
            <label htmlFor="termsCheck" className="mb-0">
              I have read and agree to the{" "}
              <a href="/terms.html" target="_blank">Terms & Conditions</a>{" "}
              and{" "}
              <a href="/privacy-policy.html" target="_blank">Privacy Policy</a>
            </label>
          </div>

          <button
            type="submit"
            className={`login-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="spinner"></div>
            ) : (
              "Sign Up"
            )}
          </button>

          <div className="signup-redirect">
            <p>Already have an account? <span onClick={navigateToLogin}>Login</span></p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignUp;

//Correct with 471 line code changes
